import { useState } from 'react';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import {
  useDiscountReasons,
  useCreateDiscountReason,
  useUpdateDiscountReason,
  useDeleteDiscountReason,
} from '@/hooks/useDiscountReasons';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ABM de motivos de descuento.
 *
 * Muestra también los desactivados: un motivo que ya se usó no se borra —se apaga—
 * porque si no, esas ventas quedarían sin poder decir por qué se descontaron.
 */
export default function DiscountReasonsModal({ isOpen, onClose }: Props) {
  const { data: motivos = [] } = useDiscountReasons(false);
  const crear = useCreateDiscountReason();
  const actualizar = useUpdateDiscountReason();
  const borrar = useDeleteDiscountReason();

  const [nuevo, setNuevo] = useState('');

  const agregar = async () => {
    const nombre = nuevo.trim();
    if (!nombre) return;
    try {
      await crear.mutateAsync(nombre);
      setNuevo('');
      toast.success(`Motivo "${nombre}" agregado`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo agregar el motivo');
    }
  };

  const quitar = async (id: number, nombre: string) => {
    try {
      await borrar.mutateAsync(id);
      toast.success(`"${nombre}" ya no aparece al cobrar`);
    } catch {
      toast.error('No se pudo quitar el motivo');
    }
  };

  const reactivar = async (id: number, nombre: string) => {
    try {
      await actualizar.mutateAsync({ id, is_active: true });
      toast.success(`"${nombre}" vuelve a estar disponible`);
    } catch {
      toast.error('No se pudo reactivar el motivo');
    }
  };

  const activos = motivos.filter((m) => m.is_active);
  const inactivos = motivos.filter((m) => !m.is_active);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Motivos de descuento">
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Son los motivos que el cajero elige al hacer un descuento. Después se ven
          agrupados en Reportes, para saber por qué se descontó en el mes.
        </p>

        <div className="flex gap-2">
          <input
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') agregar(); }}
            placeholder="Nuevo motivo (ej. Promoción)"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
          />
          <Button onClick={agregar} loading={crear.isPending} disabled={!nuevo.trim()}>
            <Plus className="h-4 w-4 mr-1" />
            Agregar
          </Button>
        </div>

        <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
          {activos.length === 0 && (
            <p className="text-sm text-gray-400 px-3 py-4 text-center">
              No hay motivos cargados. Sin al menos uno no se puede hacer descuentos.
            </p>
          )}
          {activos.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-3 py-2">
              <span className="text-sm text-gray-900">{m.name}</span>
              <button
                onClick={() => quitar(m.id, m.name)}
                title="Quitar de la lista"
                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {inactivos.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 mb-1">
              Ya no se usan (siguen en las ventas viejas)
            </p>
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
              {inactivos.map((m) => (
                <div key={m.id} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-gray-400 line-through">{m.name}</span>
                  <button
                    onClick={() => reactivar(m.id, m.name)}
                    title="Volver a usarlo"
                    className="p-1.5 rounded-lg text-taupe-600 hover:bg-cream-100"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>Listo</Button>
        </div>
      </div>
    </Modal>
  );
}
